import {
  Grid,
  GridBaseProps,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Typography,
} from "@mui/material";
import { ScoreSource, VulnerabilityScoreSource } from "~/operations";
import {
  mapDetectedByIcon,
  mapDetectedByKeys,
  mapDetectedByToolName,
  mapDetectedByValues,
} from "./lib";
import { FirstToFindTrophy } from "./FirstToFindTrophy";
import { useDetectedBy } from "./hooks/useDetectedBy";
import { Fragment } from "react/jsx-runtime";

type DetectedByProps = {
  detectedBy: ScoreSource[] | VulnerabilityScoreSource[] | undefined | null;
  widthOverride?: GridBaseProps["size"];
  heading?: boolean;
};

export function DetectedBy({ detectedBy, widthOverride }: DetectedByProps) {
  if (!detectedBy || detectedBy.length === 0) {
    return <Fragment key="no-detected-sources" />;
  }

  const { firstFound, orderSources, onlySourceIsCnspec } = useDetectedBy({
    detectedBy,
  });
  const sizes = widthOverride ? widthOverride : { xs: 12, md: 6 };

  return (
    <Grid size={sizes}>
      <Typography sx={{ textTransform: "uppercase", fontWeight: 700 }}>
        Detected By
      </Typography>

      {detectedBy.map((source) => {
        const { __typename, name, ...sources } = source;

        const orderedSources = orderSources(sources);
        const keyId = name.split(" ").join("-").toLowerCase();

        return (
          <List
            sx={{ mt: 2.5 }}
            key={keyId}
            dense
            disablePadding
            subheader={
              <ListSubheader
                disableGutters
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  color: "text.primary",
                  background: "inherit",
                  fontWeight: 600,
                }}
              >
                {mapDetectedByIcon(name)}
                <Typography sx={{ fontSize: 16, fontWeight: "inherit" }}>
                  {mapDetectedByToolName(name)}
                </Typography>
              </ListSubheader>
            }
          >
            {orderedSources
              .filter(({ key, value }) => value !== "")
              .map(({ key, value }) => (
                <ListItem
                  key={key}
                  disablePadding
                  sx={{
                    ml: 4,
                    fontSize: 12,
                    color: "text.secondary",
                    fontWeight: 700,
                  }}
                >
                  <Grid container sx={{ width: 1 }}>
                    <Grid size={4}>
                      <ListItemText>{mapDetectedByKeys(key)}</ListItemText>
                    </Grid>
                    <Grid
                      size="auto"
                      sx={{
                        display: "inline-flex",
                        flexShrink: 0,
                        alignItems: "center",
                      }}
                    >
                      <ListItemText
                        sx={{
                          textAlign: "left",
                          mr: 1,
                          color: "text.primary",
                        }}
                      >
                        {mapDetectedByValues(key, value)}
                      </ListItemText>
                      {firstFound.__typename === "ScoreSource" &&
                        !onlySourceIsCnspec && (
                          <FirstToFindTrophy
                            {...{
                              sourceKey: key,
                              sourceValue: value,
                              firstFound,
                            }}
                          />
                        )}
                    </Grid>
                  </Grid>
                </ListItem>
              ))}
          </List>
        );
      })}
    </Grid>
  );
}
